import _ from 'lodash'
import React from 'react'
import Supercluster from 'supercluster'
import { WebMercatorViewport } from 'viewport-mercator-project'

import type { Map } from 'mapbox-gl'
import { Entity } from 'shared-libs/models/entity'

import { IBaseProps } from 'browser/components/atomic-elements/atoms/base-props'

const isCoordinate = (value: unknown, limit: number) =>
  typeof value === 'number' && Math.abs(value) <= limit

export function getValidGeolocations(entity: Entity, geolocationPath: string | string[]) {
  return _.castArray(geolocationPath)
    .map((path) => entity.get(path) as any)
    .filter(
      (geolocation) =>
        geolocation &&
        isCoordinate(geolocation.latitude, 90) &&
        isCoordinate(geolocation.longitude, 180) &&
        geolocation.latitude !== 0 &&
        geolocation.longitude !== 0
    )
}

export interface IMarkerClusterLayerProps extends IBaseProps {
  map?: Map
  entities: Entity[]
  selectedEntity: object
  geolocationPath: string | string[]
  MarkerComponent: any
  markersHeight: number
  markersWidth: number
  onMouseOver?: () => void
  onMouseOut?: () => void
  onSelect?: (selection: { data: Entity }[], selectedRow: { data: Entity }) => void
  onViewportChange: (viewport: any) => void
  width?: number
  height?: number
  viewport: any
}

type SuperclusterPointFeature = Supercluster.PointFeature<Supercluster.AnyProps>
type SuperclusterFeature =
  | Supercluster.ClusterFeature<Supercluster.AnyProps>
  | SuperclusterPointFeature

interface IMarkerClusterLayerState {
  clusters: SuperclusterFeature[] | null
  clusterIndex: Supercluster | null
}

export class MarkerClusterLayer extends React.Component<IMarkerClusterLayerProps, IMarkerClusterLayerState> {
  public static defaultProps: Partial<IMarkerClusterLayerProps> = {
    onSelect: _.noop,
  }

  constructor(props: IMarkerClusterLayerProps) {
    super(props)
    this.state = {
      clusterIndex: null,
      clusters: null,
    }
  }

  public componentDidMount() {
    // some props that are injected. E.g. map might not be ready until mounted
    const clusterIndex = this.makeClusterIndex(this.props)
    const clusters = this.makeClusters(this.props, clusterIndex)
    this.setState({ clusters, clusterIndex })
  }

  public UNSAFE_componentWillReceiveProps(nextProps: IMarkerClusterLayerProps) {
    let { clusters, clusterIndex } = this.state
    if (this.props.entities !== nextProps.entities) {
      clusterIndex = this.makeClusterIndex(nextProps)
    }
    clusters = this.makeClusters(nextProps, clusterIndex!)
    this.setState({ clusters, clusterIndex })
  }

  public makeClusters(props: IMarkerClusterLayerProps, clusterIndex: Supercluster) {
    const { map, markersHeight, viewport } = props
    if (!map) {
      // cant process markers if we arent passed the map
      return null
    }
    const bounds = map.getBounds()
    // IMPORTANT: extend south by markersHeight so that marker doesn't disappare
    // when bottom of the marker hits the edge of the map
    const southEastPoint = map.project(bounds.getSouthEast())
    southEastPoint.y += markersHeight
    const southEastCoord = map.unproject(southEastPoint)
    return clusterIndex.getClusters([
      bounds.getWest(), southEastCoord.lat,
      southEastCoord.lng, bounds.getNorth()],
    Math.round(viewport.zoom))
  }

  public makeClusterIndex(props: IMarkerClusterLayerProps) {
    const { entities, geolocationPath } = props
    const features = this.makeGeojsonFeatures(entities, geolocationPath)
    return new Supercluster({
      extent: 256,
      maxZoom: 17,
      radius: 60,
    }).load(features)
  }

  public makeGeojsonFeatures(
    entities: Entity[],
    geolocationPath: string | string[]
  ): SuperclusterPointFeature[] {
    return _.flatMap(entities, (entity: Entity) =>
      getValidGeolocations(entity, geolocationPath).map(({ latitude, longitude }, pointIndex) => ({
        geometry: {
          coordinates: [longitude, latitude],
          type: 'Point',
        },
        properties: {
          entity,
          pointIndex,
        },
        type: 'Feature',
      }))
    )
  }

  public handleExpandCluster = (cluster: SuperclusterFeature) => {
    const { clusterIndex } = this.state
    const { onViewportChange, viewport } = this.props
    viewport.longitude = cluster.geometry.coordinates[0]
    viewport.latitude = cluster.geometry.coordinates[1]
    viewport.zoom = clusterIndex!.getClusterExpansionZoom(cluster.properties.cluster_id)
    onViewportChange(viewport)
  }

  public renderMarker(cluster: SuperclusterFeature, point: number[]) {
    const { clusterIndex } = this.state
    const { map, markersHeight, MarkerComponent, markersWidth, onViewportChange,
      onMouseOver, onMouseOut, onSelect, selectedEntity, viewport } = this.props
    const zoom = Math.round(viewport.zoom)
    let entities
    if (cluster.properties.cluster) {
      const clusterId = cluster.properties.cluster_id
      entities = clusterIndex!.getLeaves(clusterId, Infinity, 0)
      entities = _.map(entities, 'properties.entity')
    } else {
      entities = [cluster.properties.entity]
    }
    const firstEntity = _.first(entities) as any
    if (!firstEntity) {
      return
    }
    const key = `${firstEntity.uniqueId}-${cluster.properties.cluster ? `cluster${cluster.properties.cluster_id}` : cluster.properties.pointIndex}`
    return (
      <MarkerComponent
        cluster={cluster}
        entities={entities}
        onMouseOver={onMouseOver}
        onMouseOut={onMouseOut}
        onClick={(entity: Entity) => onSelect!([{ data: entity }], { data: entity })}
        key={key}
        map={map}
        onExpandCluster={() => this.handleExpandCluster(cluster)}
        selectedEntity={selectedEntity}
        style={{
          left: point[0] - markersWidth / 2,
          position: 'absolute',
          top: point[1] - markersHeight,
        }}
        onViewportChange={onViewportChange}
        viewport={viewport}
      />
    )
  }

  public redraw({ project }: { project: WebMercatorViewport['project'] }) {
    const { clusters } = this.state
    if (!clusters) {
      return
    }
    return _.map(clusters, (cluster) => {
      const coordinates = cluster.geometry.coordinates
      const point = project(coordinates)
      return this.renderMarker(cluster, point)
    })
  }

  public render() {
    const { width, height, style, viewport } = this.props
    const divStyle: React.CSSProperties = {
      left: 0,
      position: 'absolute',
      top: 0,
      ...style,
    }
    const { project } = new WebMercatorViewport({
      height: height ?? 1,
      latitude: viewport.latitude,
      longitude: viewport.longitude,
      width: width ?? 1,
      zoom: viewport.zoom,
    })
    return (
      <div
        ref='overlay'
        style={divStyle}
      >
        {this.redraw({ project })}
      </div>
    )
  }
}
